import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { Notify, toast } from "@/components/ui/notify";
import { useUpstreamLinks } from "@/features/lines/chain/hooks";
import { formatRelativeTime } from "@/lib/display-labels";
import type { BatchResponse } from "@/types/batches.types";
import { useSetRunOrigins } from "./hooks";
import { UpstreamLineBatches } from "./UpstreamLineBatches";

interface OriginPickerProps {
  lineId: string;
  batch: BatchResponse;
  onClose: () => void;
}

/**
 * Spec 084 (FR-008a · FR-011) — escolher a batelada de origem.
 *
 * A oferta é só das linhas DIRETAMENTE a montante: é a cadeia que a fábrica desenhou, e oferecer o
 * ancestral distante aqui convidaria a pular uma etapa sem querer. A API aceita o ancestral distante
 * quando alguém o informa de propósito — a recusa dela é o que protege a regra, não esta lista.
 */
export function OriginPicker({ lineId, batch, onClose }: OriginPickerProps) {
  const upstream = useUpstreamLinks(lineId);
  const setOrigins = useSetRunOrigins(lineId, batch.runId);
  const [selected, setSelected] = useState<string[]>(
    batch.origins.flatMap((o) => (o.runId ? [o.runId] : []))
  );
  const linhas = upstream.data?.items ?? [];

  function toggle(runId: string) {
    setSelected((prev) =>
      prev.includes(runId) ? prev.filter((id) => id !== runId) : [...prev, runId]
    );
  }

  async function salvar() {
    await setOrigins.mutateAsync(selected);
    toast.success(selected.length > 0 ? "Origem atualizada." : "Batelada registrada sem origem.");
    onClose();
  }

  return (
    <ModalOverlay onClose={onClose} title="Escolher a origem" className="max-w-lg">
      <CardHeader>
        <CardTitle>Escolher a origem</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-[13px] text-text-secondary">
          De qual batelada veio o material desta execução? O que a origem declara chega aqui por
          herança, e continua acompanhando as correções feitas lá.
        </p>
        {upstream.isError ? (
          <Notify.QueryError error={upstream.error} onRetry={() => void upstream.refetch()} />
        ) : upstream.isLoading ? (
          <p className="text-[13px] text-text-secondary">Carregando as etapas anteriores…</p>
        ) : linhas.length === 0 ? (
          <Notify.Empty
            title="Sem etapa anterior"
            description="Esta linha não recebe material de nenhuma outra na cadeia. Ligue as linhas no detalhe da linha para que a origem possa ser informada."
          />
        ) : (
          linhas.map((l) => (
            <UpstreamLineBatches
              key={l.upstreamLineId}
              lineId={l.upstreamLineId}
              lineName={l.upstreamLineName}
              selected={selected}
              onToggle={toggle}
              formatWhen={formatRelativeTime}
            />
          ))
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={setOrigins.isPending}
            onClick={() => void salvar().catch(() => undefined)}
          >
            {setOrigins.isPending ? "Salvando…" : "Salvar origem"}
          </Button>
        </div>
      </CardContent>
    </ModalOverlay>
  );
}
